import { useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router';
import type { Order, OrderStatus } from '@auto-oms/shared/types/order';
import { api, type ApiError } from '../lib/api';
import { pushToast } from '../lib/toast';
import { StatusBadge } from '../components/ui/StatusBadge';
import { ChannelBadge } from '../components/ui/ChannelBadge';
import { OrderDetailDialog } from '../components/ui/OrderDetailDialog';
import { formatDateTime, formatKRW } from '../lib/format';
import { carrierLabel, carrierTrackingUrl } from '../lib/carrierTracking';

interface LoaderData {
  orders: Order[];
}

export async function doneLoader(): Promise<LoaderData> {
  return api.get<LoaderData>('/orders?status=DONE&limit=500');
}

const STATUS_LABEL: Record<OrderStatus, string> = {
  COLLECTED: '수집완료',
  PO_SENT: '발주전송',
  TRACKING_RECEIVED: '송장수신',
  SHIPPED_TO_MALL: '출고확정',
  DONE: '완료',
  FAILED_PO: '발주실패',
  FAILED_PARSE: '파싱실패',
  FAILED_SHIP: '출고실패',
};

export function DonePage() {
  const { orders } = useLoaderData() as LoaderData;
  const { revalidate } = useRevalidator();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<OrderStatus>('TRACKING_RECEIVED');
  const [bulkChanging, setBulkChanging] = useState(false);
  const [detailOrder, setDetailOrder] = useState<Order | null>(null);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? orders.filter(
        (o) =>
          o.buyerName.toLowerCase().includes(q) ||
          o.externalOrderId.toLowerCase().includes(q) ||
          (o.trackingNumber ?? '').includes(q),
      )
    : orders;

  function toggleSelected(id: number, checked: boolean) {
    const next = new Set(selected);
    if (checked) next.add(id);
    else next.delete(id);
    setSelected(next);
  }

  async function applyBulk() {
    setBulkChanging(true);
    try {
      const ids = [...selected];
      const settled = await Promise.allSettled(
        ids.map((id) => api.patch(`/orders/${id}/status`, { status: bulkStatus }))
      );
      const ok = settled.filter((r) => r.status === 'fulfilled').length;
      const ng = settled.filter((r) => r.status === 'rejected').length;
      if (ng === 0) {
        pushToast('success', `${ok}건 상태를 "${STATUS_LABEL[bulkStatus]}"로 변경했습니다.`);
      } else {
        pushToast('error', `성공 ${ok}건, 실패 ${ng}건 — 실패 건은 상태를 확인해 주세요.`);
      }
      setSelected(new Set());
      revalidate();
    } catch (e) {
      const err = e as ApiError;
      pushToast('error', `변경 실패: ${err.message ?? '알 수 없는 오류'}`);
    } finally {
      setBulkChanging(false);
    }
  }

  return (
    <div>
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="label-overline">처리 완료</p>
          <h1 className="mt-1 text-[28px] font-light tracking-[-0.01em] text-ink-950">완료 주문</h1>
          <p className="mt-1 text-[13px] text-ink-500">
            전체 <span className="tabular text-ink-900">{orders.length}</span>건
            {q ? (
              <>
                {' '}· 검색 결과 <span className="tabular text-ink-900">{filtered.length}</span>건
              </>
            ) : null}
          </p>
        </div>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="주문자 · 주문번호 · 송장번호 검색"
          className="h-11 w-full rounded-lg border border-ink-200 bg-paper px-3.5 text-base text-ink-800 placeholder-ink-400 focus:border-coral-500 focus:outline-none focus:ring-1 focus:ring-coral-500 sm:h-9 sm:w-72 sm:px-3 sm:text-sm"
        />
      </header>

      <div className="mt-4 rounded-lg border border-ink-200 bg-ink-50">
        {/* Mobile: stacked */}
        <div className="space-y-2.5 p-3 sm:hidden">
          <div className="flex items-center gap-2">
            <span className="shrink-0 text-sm font-medium text-ink-700">일괄 상태</span>
            <select
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value as OrderStatus)}
              disabled={bulkChanging}
              className="h-10 min-w-0 flex-1 rounded-md border border-ink-200 bg-paper px-3 text-sm text-ink-800 focus:border-coral-500 focus:outline-none focus:ring-1 focus:ring-coral-500"
            >
              {(['COLLECTED', 'PO_SENT', 'TRACKING_RECEIVED', 'DONE', 'FAILED_PO', 'FAILED_PARSE', 'FAILED_SHIP'] as OrderStatus[]).map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="inline-flex h-11 w-full items-center justify-center rounded-md border border-ink-200 bg-paper text-sm font-medium text-ink-800 active:bg-ink-100 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={selected.size === 0 || bulkChanging}
            onClick={applyBulk}
          >
            {bulkChanging ? '변경 중…' : `변경 (${selected.size})`}
          </button>
        </div>

        {/* Desktop: inline */}
        <div className="hidden flex-wrap items-center gap-2 px-3 py-2 sm:flex">
          <span className="text-sm font-medium text-ink-600">일괄 상태 변경</span>
          <select
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value as OrderStatus)}
            disabled={bulkChanging}
            className="rounded border border-ink-200 bg-paper px-2 py-1 text-sm text-ink-800 focus:outline-none focus:ring-1 focus:ring-coral-500"
          >
            {(['COLLECTED', 'PO_SENT', 'TRACKING_RECEIVED', 'DONE', 'FAILED_PO', 'FAILED_PARSE', 'FAILED_SHIP'] as OrderStatus[]).map((s) => (
              <option key={s} value={s}>{STATUS_LABEL[s]}</option>
            ))}
          </select>
          <button
            type="button"
            className="btn-secondary"
            disabled={selected.size === 0 || bulkChanging}
            onClick={applyBulk}
          >
            {bulkChanging ? '변경 중…' : `선택 ${selected.size}건 변경`}
          </button>
        </div>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden">
        {filtered.length > 0 && (
          <label className="mt-3 flex cursor-pointer items-center justify-between rounded-lg border border-ink-200 bg-paper px-3 py-2.5 shadow-sm">
            <span className="flex items-center gap-2">
              <input
                type="checkbox"
                aria-label="모두 선택"
                checked={filtered.length > 0 && filtered.every((o) => selected.has(o.id))}
                onChange={(e) =>
                  setSelected(e.target.checked ? new Set(filtered.map((o) => o.id)) : new Set())
                }
                className="h-5 w-5 shrink-0 accent-brand-600"
              />
              <span className="text-sm font-medium text-ink-800">전체 선택</span>
            </span>
            <span className="text-sm text-ink-500">
              <span className="font-semibold text-ink-700">{selected.size}</span> / {filtered.length}
            </span>
          </label>
        )}

        <div className="mt-2 space-y-1.5">
          {filtered.length === 0 ? (
            <div className="rounded-lg border border-ink-200 bg-paper px-4 py-10 text-center text-sm text-ink-500">
              {q ? '검색 결과가 없습니다.' : '완료된 주문이 없습니다.'}
            </div>
          ) : (
            filtered.map((o) => (
              <article
                key={o.id}
                role="button"
                tabIndex={0}
                onClick={() => setDetailOrder(o)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailOrder(o); } }}
                className={[
                  'cursor-pointer rounded-lg border bg-paper px-3 py-3 shadow-sm active:bg-ink-50',
                  selected.has(o.id) ? 'border-coral-400 ring-1 ring-coral-100' : 'border-ink-200',
                ].join(' ')}
              >
                {/* Row 1: checkbox · badges  ·····  주문일시 ›  */}
                <div className="flex items-center gap-2">
                  <span onClick={(e) => e.stopPropagation()} className="shrink-0">
                    <input
                      type="checkbox"
                      aria-label={`주문 ${o.externalOrderId} 선택`}
                      checked={selected.has(o.id)}
                      onChange={(e) => toggleSelected(o.id, e.target.checked)}
                      className="h-5 w-5 accent-brand-600"
                    />
                  </span>
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
                    <StatusBadge status={o.status} />
                    <ChannelBadge platform={o.platform} />
                  </div>
                  <span className="shrink-0 whitespace-nowrap text-right text-xs text-ink-500">
                    {formatDateTime(o.orderedAt)}
                  </span>
                  <span className="shrink-0 text-sm text-ink-400" aria-hidden="true">›</span>
                </div>

                {/* Row 2: 상품명 · 합계 */}
                <div className="mt-2 flex items-baseline gap-2">
                  <p className="min-w-0 flex-1 truncate text-base font-semibold leading-snug text-ink-900">{o.productName}</p>
                  <span className="shrink-0 text-base font-semibold text-ink-900">{formatKRW(o.totalPrice)}</span>
                </div>

                {/* Row 3-5: 주문자 / 주문번호 / 수량 / 송장 (라벨형) */}
                <dl className="mt-2 space-y-0.5 text-sm leading-snug text-ink-700">
                  <div className="flex gap-1.5">
                    <dt className="shrink-0 text-ink-500">주문자:</dt>
                    <dd className="min-w-0 flex-1 truncate font-medium text-ink-800">{o.buyerName}</dd>
                  </div>
                  <div className="flex gap-1.5">
                    <dt className="shrink-0 text-ink-500">주문번호:</dt>
                    <dd className="min-w-0 flex-1 truncate font-mono text-ink-700" title={o.externalOrderId}>
                      {o.externalOrderId}
                    </dd>
                  </div>
                  <div className="flex gap-1.5">
                    <dt className="shrink-0 text-ink-500">수량:</dt>
                    <dd className="font-medium text-ink-800">{o.quantity}개</dd>
                  </div>
                  <div className="flex gap-1.5">
                    <dt className="shrink-0 text-ink-500">송장:</dt>
                    <dd className="min-w-0 flex-1 truncate font-mono text-ink-700">
                      <TrackingCell
                        carrier={o.trackingCarrier}
                        trackingNumber={o.trackingNumber}
                        emptyClassName="font-sans text-ink-400"
                      />
                    </dd>
                  </div>
                </dl>
              </article>
            ))
          )}
        </div>
      </div>

      {/* Desktop table */}
      <div className="mt-2 hidden overflow-hidden rounded-lg border border-ink-200 bg-paper md:block">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px] text-sm">
            <colgroup>
              <col className="w-10" />
              <col className="w-28" />
              <col className="w-20" />
              <col className="w-36" />
              <col />
              <col className="w-36" />
              <col className="w-48" />
              <col className="w-14" />
              <col className="w-24" />
              <col className="w-44" />
              <col className="w-36" />
            </colgroup>
            <thead className="bg-ink-50 text-ink-600">
              <tr>
                <th className="px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    aria-label="모두 선택"
                    checked={filtered.length > 0 && filtered.every((o) => selected.has(o.id))}
                    onChange={(e) =>
                      setSelected(e.target.checked ? new Set(filtered.map((o) => o.id)) : new Set())
                    }
                  />
                </th>
                <th className="px-3 py-2 text-center">상태</th>
                <th className="px-3 py-2 text-center">채널</th>
                <th className="px-3 py-2 text-center">주문번호</th>
                <th className="px-3 py-2 text-center">상품</th>
                <th className="px-3 py-2 text-center">수령인</th>
                <th className="px-3 py-2 text-center">주소</th>
                <th className="px-3 py-2 text-center">수량</th>
                <th className="px-3 py-2 text-center">합계</th>
                <th className="px-3 py-2 text-center">주문일시</th>
                <th className="px-3 py-2 text-center">송장번호</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-3 py-8 text-center text-sm text-ink-500">
                    {q ? '검색 결과가 없습니다.' : '완료된 주문이 없습니다.'}
                  </td>
                </tr>
              )}
              {filtered.map((o) => (
                <tr key={o.id} className="border-t border-ink-100 hover:bg-ink-50">
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      aria-label={`주문 ${o.externalOrderId} 선택`}
                      checked={selected.has(o.id)}
                      onChange={(e) => toggleSelected(o.id, e.target.checked)}
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <StatusBadge status={o.status} />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <ChannelBadge platform={o.platform} />
                  </td>
                  <td className="px-3 py-2 text-center font-mono text-xs text-ink-700">{o.externalOrderId}</td>
                  <td className="px-3 py-2 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {!!o.isAddedProduct && (
                        <span className="shrink-0 rounded border border-sky-300 bg-sky-50 px-1 py-0.5 text-xs font-medium text-sky-700">추가상품</span>
                      )}
                      <span className="font-medium truncate" title={o.productName}>{o.productName}</span>
                    </div>
                    {o.productOption && (
                      <div className="text-xs text-ink-500 truncate" title={o.productOption}>{o.productOption}</div>
                    )}
                    {(o.pCode || o.productId) && (
                      <div className="text-xs text-ink-400 font-mono truncate">
                        {[o.pCode && `품번:${o.pCode}`, o.productId && `채널품번:${o.productId}`].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">{o.recipientName}</td>
                  <td className="px-3 py-2 text-center text-xs text-ink-600">{o.recipientAddress}</td>
                  <td className="px-3 py-2 text-center">{o.quantity}</td>
                  <td className="px-3 py-2 text-center">{formatKRW(o.totalPrice)}</td>
                  <td className="px-3 py-2 text-center text-xs text-ink-500">{formatDateTime(o.orderedAt)}</td>
                  <td className="px-3 py-2 text-center font-mono text-xs text-ink-600">
                    <TrackingCell
                      carrier={o.trackingCarrier}
                      trackingNumber={o.trackingNumber}
                      emptyClassName="text-ink-300"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <OrderDetailDialog
        open={detailOrder !== null}
        order={detailOrder}
        onClose={() => setDetailOrder(null)}
      />
    </div>
  );
}

interface TrackingCellProps {
  carrier: string | null;
  trackingNumber: string | null;
  emptyClassName: string;
}

function TrackingCell({ carrier, trackingNumber, emptyClassName }: TrackingCellProps) {
  if (!trackingNumber) return <span className={emptyClassName}>—</span>;
  const url = carrierTrackingUrl(carrier, trackingNumber);
  const label = carrierLabel(carrier);
  if (!url) return <span title={label ?? undefined}>{trackingNumber}</span>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={label ? `${label} 송장 조회` : '송장 조회'}
      className="underline decoration-ink-300 underline-offset-2 hover:text-coral-600 hover:decoration-coral-500"
    >
      {trackingNumber}
    </a>
  );
}
